import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Phone,
  Mail,
  ArrowUpRight,
  Download,
  Copy,
  Check,
  HandHeart,
} from "lucide-react";

export const CTA = ({ onJoin }) => {
  const [copied, setCopied] = useState(null);

  const copy = async (val, key) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(key);
      setTimeout(() => setCopied(null), 1800);
    } catch {}
  };

  return (
    <section
      id="pledge"
      className="relative py-28 sm:py-36 bg-[color:var(--mati-sand)] overflow-hidden"
      data-testid="cta-section"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 500px at 50% 100%, rgba(200,90,50,0.14) 0%, transparent 70%)",
        }}
      />
      <div className="relative max-w-[1360px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 lg:col-span-7 bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] rounded-3xl p-10 sm:p-14 relative overflow-hidden"
          >
            <div className="absolute inset-0 ceramic-grid-dark opacity-30" />
            <div className="relative">
              <div className="eyebrow text-[color:var(--mati-clay)]">
                05 · The Pledge
              </div>
              <h2 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[0.95] tracking-tight">
                Be the{" "}
                <span className="italic text-[color:var(--mati-clay)]">
                  change
                </span>{" "}
                in Morbi.
              </h2>
              <p className="mt-7 font-sub text-base sm:text-lg opacity-85 max-w-xl leading-relaxed">
                Cleanliness is not a one-day event. It is a daily habit.
                Segregate your waste, stop littering, join a community group,
                and inspire your neighbours.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={onJoin}
                  data-testid="cta-join-drive-btn"
                  className="group inline-flex items-center justify-center gap-2 bg-[color:var(--mati-clay)] text-[color:var(--mati-sand)] px-6 py-4 rounded-full font-sub text-[13px] tracking-widest uppercase font-semibold hover:bg-[color:var(--mati-sand)] hover:text-[color:var(--mati-forest)] transition-all"
                >
                  <HandHeart className="w-4 h-4" />
                  Join a Clean-Up Drive
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                </button>
                <a
                  href="#"
                  data-testid="cta-download-poster-btn"
                  className="inline-flex items-center justify-center gap-2 border border-[color:var(--mati-sand)]/40 text-[color:var(--mati-sand)] px-6 py-4 rounded-full font-sub text-[13px] tracking-widest uppercase font-semibold hover:bg-[color:var(--mati-sand)]/10 transition-all"
                >
                  <Download className="w-4 h-4" />
                  Download Awareness Poster
                </a>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="col-span-12 lg:col-span-5 flex flex-col gap-6"
          >
            <button
              onClick={() => copy("1800-123-4567", "phone")}
              data-testid="cta-helpline-copy"
              className="group text-left bg-white border border-[color:var(--mati-border)] rounded-3xl p-8 hover:bg-[color:var(--mati-clay)] hover:text-[color:var(--mati-sand)] hover:border-transparent transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="eyebrow opacity-70">MMC Helpline</div>
                {copied === "phone" ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4 opacity-60 group-hover:opacity-100" />
                )}
              </div>
              <div className="flex items-center gap-3 mt-5">
                <Phone className="w-6 h-6" strokeWidth={1.4} />
                <div className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                  1800-123-4567
                </div>
              </div>
              <div className="mt-4 font-sub text-sm opacity-75">
                Tap to copy — 24/7 for garbage spots, missed collection, open
                dumping.
              </div>
            </button>

            <button
              onClick={() => copy("swachh.morbi@mmcgujarat.in", "email")}
              data-testid="cta-email-copy"
              className="group text-left bg-[color:var(--mati-sand-2)] border border-[color:var(--mati-border)] rounded-3xl p-8 hover:bg-[color:var(--mati-forest)] hover:text-[color:var(--mati-sand)] hover:border-transparent transition-all"
            >
              <div className="flex items-center justify-between">
                <div className="eyebrow opacity-70">Official Email</div>
                {copied === "email" ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4 opacity-60 group-hover:opacity-100" />
                )}
              </div>
              <div className="flex items-center gap-3 mt-5">
                <Mail className="w-6 h-6" strokeWidth={1.4} />
                <div className="font-display text-2xl sm:text-3xl font-bold tracking-tight break-all">
                  swachh.morbi@mmcgujarat.in
                </div>
              </div>
              <div className="mt-4 font-sub text-sm opacity-75">
                Report, request ward programs, or volunteer with your group.
              </div>
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
